import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DateRangePicker, defaultStaticRanges } from 'react-date-range';
import { enUS } from 'date-fns/locale';
import { startOfYear, endOfYear, addYears, isSameDay } from 'date-fns';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import moment from 'moment/moment';
import { NavigateBefore, NavigateNext } from '@mui/icons-material';
import { Checkbox, Popper } from '@mui/material';
import { scheduleStyle } from './styles';
import { ACTIVE_CLIENT_STAFF_TOOLTIP } from './constants';
import {
    CheckboxesTags,
    CloseButton,
    CustomizedSwitch,
    DownloadCsv,
    ManageActions,
    SelectAutocompletePaginationMultiple,
    SelectTypeAutocomplete,
    SimpleTooltip,
} from 'components';
import { useDispatch, useSelector } from 'react-redux';
import { fundingSourceActions } from 'store';
import {
    getApptErrorOptions,
    useDiagnosisMatchEnabled,
    clientStatusEnums,
    Images,
    renderClientName,
    renderStaffName,
    SaveParams,
    staffStatusEnums,
    User,
} from 'utils';
import { checkCreateApptPermissions, localSaveScheduleDates } from '../constants';

export const Filters = ({ handleOpen }) => {
    const diagnosisMatchEnabled = useDiagnosisMatchEnabled();
    moment.locale('en', {
        week: {
            dow: 0,
        },
    });
    const customLocale = {
        ...enUS,
        options: {
            ...enUS.options,
            weekStartsOn: 0,
        },
    };

    const toLocalDate = (dateStr) => {
        const m = moment(dateStr);
        return new Date(m.year(), m.month(), m.date());
    };
    const currentDate = moment();
    const currentWeekStart = currentDate.clone().startOf('week');
    const currentWeekEnd = currentDate.clone().endOf('week');
    const classes = scheduleStyle();
    const navigate = useNavigate();
    const location = useLocation();
    const info = location?.state;
    const { userInfo } = User();
    const dispatch = useDispatch();
    const fundingSourceList = useSelector((state) => state.fundingSource.fundingSourceList);

    useEffect(() => {
        dispatch(fundingSourceActions.getFundingSource({ sortType: 'name', orderType: 'ASC' }));
    }, [dispatch]);

    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const id = open ? 'simple-popper' : undefined;
    const isCalendar = info?.viewType === 'calendar';

    const customStaticRanges = [
        ...defaultStaticRanges,
        {
            label: 'This Year',
            range: () => ({
                startDate: startOfYear(new Date()),
                endDate: endOfYear(new Date()),
            }),
            isSelected(range) {
                const definedRange = this.range();
                return (
                    isSameDay(range.startDate, definedRange.startDate) &&
                    isSameDay(range.endDate, definedRange.endDate)
                );
            },
        },
        {
            label: 'Last Year',
            range: () => ({
                startDate: addYears(new Date(), -1),
                endDate: new Date(),
            }),
            isSelected(range) {
                const definedRange = this.range();
                return (
                    isSameDay(range.startDate, definedRange.startDate) &&
                    isSameDay(range.endDate, definedRange.endDate)
                );
            },
        },
    ];
    const activeFiltersCount = (info?.staffs?.length ? 1 : 0) +
        (info?.clients?.length ? 1 : 0) +
        (info?.payer ? 1 : 0) +
        (info?.errors?.length ? 1 : 0) +
        (info?.authBillable !== undefined ? 1 : 0) +
        (info?.withoutNotes ? 1 : 0) +
        (info?.supervision ? 1 : 0) +
        (info?.withoutSignature ? 1 : 0) +
        (info?.withoutClientSignature ? 1 : 0) +
        (info?.withoutProviderSignature ? 1 : 0);
    const calendarActiveFiltersCount = (info?.staffs?.length ? 1 : 0) +
        (info?.clients?.length ? 1 : 0) +
        (info?.types?.length ? 1 : 0) +
        (info?.statuses?.length ? 1 : 0) +
        (info?.errors?.length ? 1 : 0) +
        (info?.supervision ? 1 : 0) +
        (info?.withoutNotes ? 1 : 0) +
        (info?.withoutSignature ? 1 : 0) +
        (info?.withoutClientSignature ? 1 : 0) +
        (info?.withoutProviderSignature ? 1 : 0) +
        (info?.cptCode?.length ? 1 : 0);
    // const [state, setState] = useState([{
    //     startDate: info?.firstDate ? new Date(info.firstDate) : currentWeekStart.toDate(),
    //     endDate: info?.lastDate ? new Date(info.lastDate) : currentWeekEnd.toDate(),
    //     key: 'selection',
    // }]);
    const [state, setState] = useState([ {
        startDate: new Date(),
        endDate: new Date(),
        key: 'selection',
    },]);

    // Keep the dropdown calendar in sync when the left-panel calendar changes the date range
    useEffect(() => {
        const start = info?.firstDate ? toLocalDate(info.firstDate) : currentWeekStart.toDate();
        const end = info?.lastDate ? toLocalDate(info.lastDate) : currentWeekEnd.toDate();
        setState([{ startDate: start, endDate: end, key: 'selection' }]);
    }, [info?.firstDate, info?.lastDate]);
    const openCalendarSelector = (event) => {
        setAnchorEl(anchorEl ? null : event.currentTarget);
    };
    const handleDateChange = (item) => {
        const startDate = moment(item?.selection?.startDate);
        const endDate = moment(item?.selection?.endDate);
        const stateParams = {
            startDate: toLocalDate(startDate),
            endDate: toLocalDate(endDate),
            key: 'selection',
        };
        stateParams.firstDate = startDate.format('YYYY-MM-DD');
        stateParams.lastDate = endDate.format('YYYY-MM-DD');
        setState([stateParams]);

        const params = {
            ...info,
        };
        params.firstDate = startDate.format('YYYY-MM-DD');
        params.lastDate = endDate.format('YYYY-MM-DD');
        params.page = 1;
        params.limit = 50;
        params.skip = 0;
        localSaveScheduleDates({
            firstDate: params?.firstDate ? params?.firstDate : null,
            lastDate: params?.lastDate ? params?.lastDate : null,
        });
        SaveParams(navigate, params);
    };

    const handleClear = () => {
        setState([
            {
                startDate: toLocalDate(moment()),
                endDate: toLocalDate(moment()),
                key: 'selection',
            },
        ]);

        const params = {
            ...info,
        };

        delete params.firstDate;
        delete params.lastDate;
        params.page = 1;
        params.limit = 50;
        params.skip = 0;

        localSaveScheduleDates({
            firstDate: null,
            lastDate: null,
        });
        SaveParams(navigate, params);
    };

    const handleResetAllFilters = (handleClose) => {
        const params = {
            ...info,
        };
        delete params.staffs;
        delete params.staffFull;
        delete params.clients;
        delete params.clientFull;
        delete params.payer;
        delete params.payerFull;
        delete params.errors;
        delete params.authBillable;
        delete params.withoutNotes;
        delete params.withoutSignature;
        delete params.withoutClientSignature;
        delete params.withoutProviderSignature;
        delete params.supervision;
        params.page = 1;
        params.limit = 50;
        params.skip = 0;
        SaveParams(navigate, params);

        if (handleClose) {
            handleClose();
        }
    };

    const handleChange = (e) => {
        const newFilters = {
            ...info,
        };
        newFilters.page = 1;
        newFilters.skip = 0;

        if (e?.target?.value === 'All') {
            delete newFilters[e.target.name];
        } else {
            newFilters[e.target.name] = e.target.value;
            if (e.target.name === 'payer') {
                const currentPayer = fundingSourceList?.funders?.find(f => f.id === e.target.value);
                if (currentPayer) newFilters.payerFull = currentPayer;
            } else {
                if (e.target.name === 'staffs' || e.target.name === 'clients') {
                    newFilters[
                        e.target.name === 'staffs' ? 'staffFull' :
                            e.target.name === 'clients' ? 'clientFull' :
                                ''] = e.target?.full;
                }
            }
        }
        SaveParams(navigate, { ...newFilters });
    };

    const handleSelectValue = (list, name) => {
        const params = {
            ...info,
        };
        params.page = 1;
        params.skip = 0;
        list?.length ? (params[name] = list) : delete params[name];
        SaveParams(navigate, { ...params });
    };

    const handleChangeScreen = (type) => {
        // closeFullScreen && closeFullScreen();
        const pushInfo = {
            ...info,
            viewType: type,
        };
        delete pushInfo.screenType;
        delete pushInfo.statuses;
        delete pushInfo.cptCode;
        delete pushInfo.types;

        if (type === 'calendar' && (!pushInfo?.staffs || pushInfo?.staffs?.length === 0)) {
            pushInfo.ownStaff = true;
            pushInfo.staffs = [userInfo?.id];
            pushInfo.staffFull = [
                {
                    id: userInfo?.id,
                    firstName: userInfo?.firstName,
                    lastName: userInfo?.lastName,
                },
            ];
        }

        if (type === 'list' && pushInfo?.ownStaff && pushInfo?.staffFull?.length === 1) {
            const ownStaff = pushInfo?.staffFull?.find(s => s?.id === userInfo?.id);

            if (ownStaff) {
                delete pushInfo.staffs;
                delete pushInfo.staffFull;
                delete pushInfo.ownStaff;
            }
        }

        SaveParams(navigate, { ...pushInfo });
    };

    const changeShowActive = () => {
        const pushInfo = {
            ...info,
            showActive: !info?.showActive,
        };
        SaveParams(navigate, { ...pushInfo });
    };
    const goToNextWeek = () => {
        const date1 = moment(info?.firstDate);
        const date2 = moment(info?.lastDate);
        const daysDifference = date2.diff(date1, 'days') + 1;
        const updatedFirstDate = info?.firstDate
            ? moment(info?.firstDate).add(daysDifference, 'days').format('YYYY-MM-DD')
            : moment(info?.firstDate ? info?.firstDate : currentWeekStart)
                .clone()
                .add(1, 'weeks')
                .startOf('week')
                .format('YYYY-MM-DD');
        const updatedLastDate = info?.lastDate
            ? moment(info?.lastDate).add(daysDifference, 'days').format('YYYY-MM-DD')
            : moment(info?.firstDate ? info?.firstDate : currentWeekStart)
                .clone()
                .add(1, 'weeks')
                .endOf('week')
                .format('YYYY-MM-DD');

        const params = {
            ...info,
            firstDate: updatedFirstDate,
            lastDate: updatedLastDate,
        };

        params.limit = 50;
        params.skip = 0;
        params.page = 1;

        navigate(location?.pathname, {
            state: params,
        });

        localSaveScheduleDates({
            firstDate: updatedFirstDate,
            lastDate: updatedLastDate,
        });
    };

    const goToLastWeek = () => {
        const date1 = moment(info?.firstDate);
        const date2 = moment(info?.lastDate);
        const daysDifference = date2.diff(date1, 'days') + 1;
        const updatedFirstDate = info?.firstDate
            ? moment(info?.firstDate).subtract(daysDifference, 'days').format('YYYY-MM-DD')
            : moment(info?.firstDate ? info?.firstDate : currentWeekStart)
                .clone()
                .subtract(1, 'weeks')
                .startOf('week')
                .format('YYYY-MM-DD');
        const updatedLastDate = info?.lastDate
            ? moment(info?.lastDate).subtract(daysDifference, 'days').format('YYYY-MM-DD')
            : moment(info?.firstDate ? info?.firstDate : currentWeekStart)
                .clone()
                .subtract(1, 'weeks')
                .endOf('week')
                .format('YYYY-MM-DD');

        const params = {
            ...info,
            firstDate: updatedFirstDate,
            lastDate: updatedLastDate,
        };

        params.limit = 50;
        params.skip = 0;
        params.page = 1;

        navigate(location?.pathname, {
            state: params,
        });

        localSaveScheduleDates({
            firstDate: updatedFirstDate,
            lastDate: updatedLastDate,
        });
    };

    const navigateToday = () => {
        const params = {
            ...info,
            firstDate: moment().format('YYYY-MM-DD'),
            lastDate: moment().format('YYYY-MM-DD'),
        };

        params.limit = 50;
        params.skip = 0;
        params.page = 1;

        navigate(location?.pathname, {
            state: params,
        });

        // navigate({
        //     state: {
        //         ...info,
        //         firstDate: moment().utc().format(),
        //         lastDate: moment().utc().format(),
        //     },
        // });
    };

    // const currentWeek =
    //     `${moment(info?.firstDate ? info?.firstDate : currentWeekStart).format('MMMM D')}
    //    –
    //      ${moment(info?.lastDate ? info?.lastDate : currentWeekEnd).format('MMMM D')}`;
    //
    const renderTabBtns = () => {
        return (
            <div className={classes.scheduleViewBtnsBox}>
                <button
                    className={
                        info?.viewType === 'list' || !info?.viewType ? classes.scheduleViewActiveBtn : classes.scheduleViewPassiveBtn
                    }
                    onClick={() => handleChangeScreen('list')}
                >
                    <img src={Images.scheduleList} alt="icon" />
                </button>

                <button
                    className={info?.viewType === 'calendar' ? classes.scheduleViewActiveBtn : classes.scheduleViewPassiveBtn}
                    onClick={() => handleChangeScreen('calendar')}
                >
                    <img src={Images.scheduleGroup} alt="icon" />
                </button>
            </div>
        );
    };

    const createEventBtn = () => {
        return (
            <div className="flex-align-center" style={{ gap: 16 }}>
                <DownloadCsv type={'schedule'} fromModal={true} />
                {checkCreateApptPermissions(userInfo) && (
                    <div onClick={handleOpen} className={classes.addEvent}>
                        <img src={Images.addCircle} alt="icon" />
                        <p>Add</p>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="schedule-filters">
            <div className="schedule-filter-wrapper">
                <div className="schedule-filter-box">
                    {isCalendar && (
                        <button
                            className={`filter-passive-btn ${info?.showCalendarFilters !== false ? 'active-filter' : ''}`}
                            onClick={() => {
                                const newFilters = { ...info, showCalendarFilters: info?.showCalendarFilters === false ? true : false };
                                SaveParams(navigate, newFilters);
                            }}
                            style={{ position: 'relative' }}>
                            {info?.showCalendarFilters === false && calendarActiveFiltersCount > 0 && (
                                <div className="active-filters-count-badge">{calendarActiveFiltersCount}</div>
                            )}
                            <img src={Images.filters} alt="icon" />
                        </button>
                    )}
                    {!isCalendar && (
                        <ManageActions
                            styleBodyItems={{ padding: 0 }}
                            btnClass={`filter-passive-btn ${activeFiltersCount > 0 ? 'active-filter' : ''}`}
                            renderBtnView={(anchorEl) => (
                                <div>
                                    {activeFiltersCount > 0 && <div className="active-filters-count-badge">{activeFiltersCount}</div>}
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <img src={Images.filters} alt="icon" />
                                    </div>
                                </div>
                            )}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                            wrapperStyles={{ position: 'relative' }}
                            renderBody={(handleClose) => (
                                <div style={{ minWidth: '280px' }}>
                                    <div className="schedule-filter-popper-header">
                                        <div className="flex-align-center" style={{ gap: 8 }}>
                                            <img src={Images.filters} alt="icon" />
                                            <p>Filters</p>
                                        </div>
                                        <CloseButton handleCLic={handleClose} />
                                    </div>

                                    <div className="schedule-filter-popper-body">
                                        <div className="filter-popper-body-selectors">
                                            <SelectAutocompletePaginationMultiple
                                                defaultStatuses={!info?.showActive ? [staffStatusEnums.ACTIVE] : null}
                                                outLabel={'Staff'}
                                                uiType={'tableFilter'}
                                                placeholder={
                                                    info?.staffFull?.length
                                                        ? info?.staffFull?.map((i) => renderStaffName(i)).join(', ')
                                                        : 'All'
                                                }
                                                name={'staffs'}
                                                handleSelect={handleChange}
                                                defaultValue={info?.staffFull ? info?.staffFull : []}
                                                renderValue={(i) => renderStaffName(i)}
                                                type={'staff'}
                                                noError={true}
                                                autoSelect={true}
                                                forceShowActive={info?.showActive}
                                            />
                                            <SelectAutocompletePaginationMultiple
                                                defaultStatuses={
                                                    !info?.showActive
                                                        ? [
                                                              clientStatusEnums.ACTIVE,
                                                              clientStatusEnums.FBA,
                                                              clientStatusEnums.ON_HOLD,
                                                              clientStatusEnums.WAIT_LIST,
                                                          ]
                                                        : null
                                                }
                                                outLabel={'Client'}
                                                uiType={'tableFilter'}
                                                placeholder={
                                                    info?.clientFull?.length
                                                        ? info?.clientFull?.map((i) => renderClientName(i)).join(', ')
                                                        : 'All'
                                                }
                                                name={'clients'}
                                                handleSelect={handleChange}
                                                defaultValue={info?.clientFull ? info?.clientFull : []}
                                                renderValue={(i) => renderClientName(i)}
                                                type={'client'}
                                                noError={true}
                                                autoSelect={true}
                                                forceShowActive={info?.showActive}
                                            />
                                            <SelectTypeAutocomplete
                                                placeholder={info?.payer ? '' : 'All'}
                                                name={'payer'}
                                                handleSelect={handleChange}
                                                defaultValue={info?.payer}
                                                list={
                                                    fundingSourceList?.funders
                                                        ? [{ id: 'All', name: 'All' }, ...fundingSourceList?.funders]
                                                        : [{ id: 'All', name: 'All' }]
                                                }
                                                renderValue={(i) => i?.name}
                                                uiType={'tableFilter'}
                                                outLabel={'Payer'}
                                                noError={true}
                                            />
                                            <CheckboxesTags
                                                handleChange={(e) => handleSelectValue(e, 'errors')}
                                                name="errors"
                                                renderValue={(i) => i?.name}
                                                value={info?.errors ? info?.errors : []}
                                                permissionsList={getApptErrorOptions(diagnosisMatchEnabled)}
                                                outLabel={'Errored'}
                                                placeholder={info?.errors?.length ? info?.errors?.map((i) => i?.name).join(', ') : 'All'}
                                                uiType={'tableFilter'}
                                                notShowError={true}
                                                styles={{ width: '100%' }}
                                            />
                                        </div>

                                        <div className="schedule-filter-checkboxes-wrapper">
                                            <div className="check-box-filter" style={{ marginLeft: 0 }}>
                                                <Checkbox
                                                    onClick={() =>
                                                        handleChange({
                                                            target: {
                                                                value: !info?.withoutNotes,
                                                                name: 'withoutNotes',
                                                            },
                                                        })
                                                    }
                                                    style={{
                                                        padding: 0,
                                                        color: info?.withoutNotes ? '#347AF0' : '#D0D5DD',
                                                        borderWidth: 1,
                                                    }}
                                                    checked={info?.withoutNotes || false}
                                                />
                                                <p style={{ margin: 0 }}>Without Notes</p>
                                            </div>
                                            <div className="check-box-filter" style={{ marginLeft: 0 }}>
                                                <Checkbox
                                                    onClick={() =>
                                                        handleChange({
                                                            target: {
                                                                value: !info?.withoutSignature,
                                                                name: 'withoutSignature',
                                                            },
                                                        })
                                                    }
                                                    style={{
                                                        padding: 0,
                                                        color: info?.withoutSignature ? '#347AF0' : '#D0D5DD',
                                                        borderWidth: 1,
                                                    }}
                                                    checked={info?.withoutSignature || false}
                                                />
                                                <p style={{ margin: 0 }}>Without Signatures</p>
                                            </div>
                                            <div className="check-box-filter" style={{ marginLeft: 0 }}>
                                                <Checkbox
                                                    onClick={() =>
                                                        handleChange({
                                                            target: {
                                                                value: !info?.withoutClientSignature,
                                                                name: 'withoutClientSignature',
                                                            },
                                                        })
                                                    }
                                                    style={{
                                                        padding: 0,
                                                        color: info?.withoutClientSignature ? '#347AF0' : '#D0D5DD',
                                                        borderWidth: 1,
                                                    }}
                                                    checked={info?.withoutClientSignature || false}
                                                />
                                                <p style={{ margin: 0 }}>Without Client Signature</p>
                                            </div>
                                            <div className="check-box-filter" style={{ marginLeft: 0 }}>
                                                <Checkbox
                                                    onClick={() =>
                                                        handleChange({
                                                            target: {
                                                                value: !info?.withoutProviderSignature,
                                                                name: 'withoutProviderSignature',
                                                            },
                                                        })
                                                    }
                                                    style={{
                                                        padding: 0,
                                                        color: info?.withoutProviderSignature ? '#347AF0' : '#D0D5DD',
                                                        borderWidth: 1,
                                                    }}
                                                    checked={info?.withoutProviderSignature || false}
                                                />
                                                <p style={{ margin: 0 }}>Without Provider Signature</p>
                                            </div>

                                            <div className="check-box-filter" style={{ marginLeft: 0 }}>
                                                <Checkbox
                                                    onClick={() =>
                                                        handleChange({
                                                            target: {
                                                                value: !info?.supervision,
                                                                name: 'supervision',
                                                            },
                                                        })
                                                    }
                                                    style={{
                                                        padding: 0,
                                                        color: info?.supervision ? '#347AF0' : '#D0D5DD',
                                                        borderWidth: 1,
                                                    }}
                                                    checked={info?.supervision || false}
                                                />
                                                <p style={{ margin: 0 }}>Supervision</p>
                                            </div>
                                            <SimpleTooltip
                                                title={
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                                        <p>{ACTIVE_CLIENT_STAFF_TOOLTIP.ON}</p>
                                                        <p>{ACTIVE_CLIENT_STAFF_TOOLTIP.OFF}</p>
                                                    </div>
                                                }
                                                placement="right">
                                                <div className="check-box-filter" style={{ width: 'fit-content' }}>
                                                    <Checkbox
                                                        onClick={() =>
                                                            handleChange({
                                                                target: {
                                                                    value: !info?.showActive,
                                                                    name: 'showActive',
                                                                },
                                                            })
                                                        }
                                                        style={{
                                                            padding: 0,
                                                            color: !info?.showActive ? '#347AF0' : '#D0D5DD',
                                                            borderWidth: 1,
                                                        }}
                                                        checked={!info?.showActive}
                                                    />
                                                    <p>Active clients/staff only</p>
                                                </div>
                                            </SimpleTooltip>
                                        </div>
                                    </div>

                                    <div className="schedule-filter-popper-footer">
                                        <button
                                            disabled={activeFiltersCount === 0}
                                            style={{
                                                opacity: activeFiltersCount > 0 ? 1 : 0.5,
                                                pointerEvents: activeFiltersCount > 0 ? 'auto' : 'none',
                                            }}
                                            onClick={() => handleResetAllFilters(handleClose)}>
                                            <img src={Images.revertWhite} alt="reset" />
                                            <p>Reset all</p>
                                        </button>

                                        <p className="schedule-filter-footer-result">
                                            {activeFiltersCount} result{activeFiltersCount !== 1 ? 's' : ''}
                                        </p>
                                    </div>
                                </div>
                            )}
                        />
                    )}
                    <div>{renderTabBtns()}</div>
                    <button className="filter-passive-btn" style={{ width: 'fit-content' }} onClick={navigateToday}>
                        <p>Today</p>
                    </button>
                    <div className={classes.calendarNextPrewButtons}>
                        <div className={classes.buttonsWrapper}>
                            <div className={classes.navigationButtons}>
                                <NavigateBefore style={{ color: '#387DFF', cursor: 'pointer' }} onClick={() => goToLastWeek()} />
                                <NavigateNext style={{ color: '#387DFF', cursor: 'pointer' }} onClick={() => goToNextWeek()} />
                            </div>
                            <button aria-describedby={id} className={classes.dateStyle} onClick={openCalendarSelector}>
                                <span>{`${moment(info?.firstDate ? info?.firstDate : currentWeekStart).format(
                                    moment(info?.firstDate ? info?.firstDate : currentWeekStart).year() ===
                                        moment(info?.lastDate ? info?.lastDate : currentWeekEnd).year()
                                        ? 'MMM D'
                                        : 'MMM D, YYYY'
                                )} – ${moment(info?.lastDate ? info?.lastDate : currentWeekEnd).format('MMM D, YYYY')}`}</span>
                                <img src={Images.dropdownArrow} alt="down" />
                            </button>
                        </div>
                    </div>
                </div>
                <>{createEventBtn()}</>
            </div>

            <div>{/* filters are inside ManageActions popover */}</div>

            <Popper
                id={id}
                open={open}
                anchorEl={anchorEl}
                className="schedule-date-selector-box"
                placement="bottom-start"
                style={{ position: 'absolute', zIndex: 1500 }}>
                <DateRangePicker
                    showDateDisplay={false}
                    ranges={state}
                    onChange={handleDateChange}
                    locale={customLocale}
                    staticRanges={customStaticRanges}
                />
                <button style={{ marginTop: '-27px' }} className="clear-bnt" onClick={handleClear}>
                    Clear
                </button>
            </Popper>
            {open && <div className="beck-drop" onClick={() => setAnchorEl(null)} />}
        </div>
    );
};
